import { gunzipSync, Zip, ZipPassThrough } from 'fflate';
import FileSaver from 'file-saver';
import CaseDef from '../types/CaseDef';
import * as p10Staging from '../opfs/p10-staging';
import Instance from '../types/Instance';
import { addLog, progressBar, updateStatus } from '../logger';
import { collapseSeriesParts } from '../display-sets';

async function loadInstanceBytes(instance: Instance): Promise<Uint8Array | null> {
  if (instance.instanceData.p10Staged) {
    return p10Staging.loadUncompressed(instance.uid);
  }
  const blob = instance.instanceData.dicomP10Compressed;
  if (blob) {
    return gunzipSync(new Uint8Array(await blob.arrayBuffer()));
  }
  const buf = instance.instanceData.dicomP10AnonymizedArrayBuffer;
  if (buf) return new Uint8Array(buf);
  return null;
}

function sanitize(name: string | undefined, fallback: string): string {
  return name?.replace(/[^a-z0-9]/gi, '_') || fallback;
}

// Minimal local typing — TS lib.dom may lack File System Access API depending on version.
interface WritableStreamLike {
  write(data: Uint8Array): Promise<void>;
  close(): Promise<void>;
  abort?(reason?: any): Promise<void>;
}
interface FileHandleLike {
  createWritable(): Promise<WritableStreamLike>;
}
type SavePicker = (opts: {
  suggestedName?: string;
  types?: { description?: string; accept: Record<string, string[]> }[];
}) => Promise<FileHandleLike>;

async function openSaveStream(fileName: string): Promise<WritableStreamLike | null> {
  const picker = (window as any).showSaveFilePicker as SavePicker | undefined;
  if (typeof picker !== 'function') return null;
  try {
    const handle = await picker({
      suggestedName: fileName,
      types: [{ description: 'ZIP archive', accept: { 'application/zip': ['.zip'] } }],
    });
    return await handle.createWritable();
  } catch (err) {
    // User cancelled — surface as null and let caller decide; other errors bubble.
    if ((err as DOMException)?.name === 'AbortError') return null;
    throw err;
  }
}

export async function downloadAll(caseDef: CaseDef) {
  const fileName = `anonymized-images-${new Date().getTime()}.zip`;
  let writable: WritableStreamLike | null = null;

  try {
    const numInstances = caseDef.studies.reduce((total, study) => {
      study.series.forEach((ser) => {
        total += ser.instances.length;
      });
      return total;
    }, 0);

    // Ask for a save location BEFORE we start work — keeps the user gesture
    // chain intact and lets us bail cheaply if they cancel.
    try {
      writable = await openSaveStream(fileName);
      // If the picker exists and returned null, the user cancelled.
      if (writable === null && typeof (window as any).showSaveFilePicker === 'function') {
        updateStatus('Download cancelled');
        return;
      }
    } catch (err) {
      // Picker present but threw a non-cancel error — log and fall back to Blob path.
      console.warn('showSaveFilePicker failed, falling back to in-memory blob', err);
      writable = null;
    }

    progressBar.value.start(numInstances);
    updateStatus('Generating zip file');

    // When streaming to disk we serialise writes through a chain to preserve
    // ordering and apply backpressure. When falling back, we accumulate chunks.
    const zipChunks: Uint8Array[] | null = writable ? null : [];
    let writeChain: Promise<void> = Promise.resolve();
    let zipError: Error | null = null;
    let zipDone = false;
    let resolveDone!: () => void;
    const donePromise = new Promise<void>((res) => {
      resolveDone = res;
    });

    const zipStream = new Zip((err, chunk, final) => {
      if (err) {
        zipError = err;
        resolveDone();
        return;
      }
      if (chunk) {
        if (writable) {
          const buf = chunk;
          const w = writable;
          writeChain = writeChain.then(() => w.write(buf)).catch((e) => {
            zipError = e instanceof Error ? e : new Error(String(e));
          });
        } else {
          zipChunks!.push(chunk);
        }
      }
      if (final) {
        zipDone = true;
        resolveDone();
      }
    });

    for (const [studyIdx, study] of caseDef.studies.entries()) {
      const studyDir = sanitize(study.studyDescription, `Study ${studyIdx + 1}`);
      // Collapse split parts back into one directory per source series: they
      // share a description, so writing them separately would land two parts in
      // the same folder with instance filenames both numbered from 1. See
      // display-sets.ts.
      for (const [seriesIdx, series] of collapseSeriesParts(study.series).entries()) {
        const seriesDir = sanitize(series.seriesDescription, `Series ${seriesIdx + 1}`);
        for (const [instIdx, instance] of series.instances.entries()) {
          const bytes = await loadInstanceBytes(instance);
          progressBar.value.numDone += 1;
          if (!bytes) continue;
          const entry = new ZipPassThrough(`${studyDir}/${seriesDir}/${instIdx + 1}.dcm`);
          zipStream.add(entry);
          entry.push(bytes, true);
          if (zipError) throw zipError;
        }
      }
    }

    zipStream.end();
    await donePromise;
    await writeChain;
    if (zipError) throw zipError;
    if (!zipDone) throw new Error('Zip stream ended without final chunk');

    if (writable) {
      await writable.close();
      writable = null;
    } else {
      FileSaver.saveAs(new Blob(zipChunks as unknown as BlobPart[]), fileName);
    }

    progressBar.value.reset();
    updateStatus('Done.');
  } catch (err) {
    if (writable) {
      try {
        await writable.abort?.(String(err));
      } catch {
        // best-effort
      }
    }
    const errMessage = (err as any).message || (err as any).error?.message;
    if (errMessage && typeof errMessage === "string") {
      addLog("error", `Error generating zip: ${errMessage}`);
    }
    console.error(err);
  }
}
